/**
 * Bar chart component for KPI Trend Modal.
 * Extracted from KPITrendModal.tsx for better code organization.
 */

import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme/useTheme';
import { formatKpiValueByName, KpiFormat } from '../utils/formatters';

interface DetailBarChartProps {
	labels: string[];
	values: number[];
	format: KpiFormat;
	metricName: string;
	/** When provided, overrides the default bar color. Used in dark mode to
	 *  reflect the KPI's category accent color. */
	barColor?: string;
}

const formatChartLabel = (label: string): string => {
	// Convert dd-mm-yyyy to dd/mm
	const dateMatch = label.match(/^(\d{2})-(\d{2})-\d{4}$/);
	if (dateMatch) {
		return `${dateMatch[1]}/${dateMatch[2]}`;
	}
	return label;
};

const DetailBarChart: React.FC<DetailBarChartProps> = ({ labels, values, format, metricName, barColor }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	const safeValues = values.length > 0 ? values : [0];
	const safeLabels = labels.length > 0 ? labels : safeValues.map((_, i) => String(i + 1));
	const maxValue = Math.max(...safeValues, 1);

	const getBarHeight = (value: number) => Math.max((value / maxValue) * 200, 2);

	return (
		<View style={styles.chartContainer}>
			{/* Y-axis */}
			<View style={styles.yAxisContainer}>
				<Text style={[styles.yAxisLabel, isDark && { color: colors.text.secondary }]}>{formatKpiValueByName(maxValue, format, metricName)}</Text>
				<Text style={[styles.yAxisLabel, isDark && { color: colors.text.secondary }]}>0</Text>
			</View>

			{/* Bars + X-axis */}
			<View style={styles.chartContent}>
				<View style={[styles.barsContainer, isDark && { borderBottomColor: colors.border.default }]}>
				{safeValues.map((value, index) => (
					<View key={index} style={styles.barWrapper}>
						<Text style={[styles.barValueLabel, isDark && { color: colors.text.primary }]}>{formatKpiValueByName(value, format, metricName)}</Text>
						<View style={styles.barContainer}>
							<View style={[styles.bar, { height: getBarHeight(value), backgroundColor: barColor ?? '#41B8E6' }]} />
						</View>
					</View>
				))}
				</View>

				<View style={styles.xAxisContainer}>
					{safeLabels.map((label, index) => (
						<View key={index} style={styles.xAxisLabelWrapper}>
							<Text style={[styles.xAxisLabel, isDark && { color: colors.text.secondary }]} numberOfLines={1}>{formatChartLabel(label)}</Text>
						</View>
					))}
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	chartContainer: {
		flexDirection: 'row',
		gap: 12,
		marginTop: '2%',
	},
	yAxisContainer: {
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		marginBottom: 22,
		marginTop: -6,
	},
	yAxisLabel: {
		fontSize: 11,
		fontWeight: '400',
		color: '#616161',
		fontFamily: FontFamily.regular,
	},
	chartContent: {
		flex: 1,
	},
	barsContainer: {
		flexDirection: 'row',
		height: 200,
		alignItems: 'flex-end',
		borderBottomWidth: 1,
		borderBottomColor: '#E0E0E0',
		gap: 4,
	},
	barWrapper: {
		flex: 1,
		alignItems: 'center',
		minWidth: 16,
	},
	barValueLabel: {
		fontSize: 10,
		fontWeight: '600',
		color: '#3D4147',
		fontFamily: FontFamily.medium,
		marginBottom: 4,
		textAlign: 'center',
	},
	barContainer: {
		width: '100%',
		justifyContent: 'flex-end',
		alignItems: 'center',
	},
	bar: {
		width: '60%',
		borderTopLeftRadius: 4,
		borderTopRightRadius: 4,
		minHeight: 2,
	},
	xAxisContainer: {
		flexDirection: 'row',
		marginTop: 8,
		gap: 4,
	},
	xAxisLabelWrapper: {
		flex: 1,
		alignItems: 'center',
		minWidth: 16,
	},
	xAxisLabel: {
		fontSize: 10,
		fontWeight: '400',
		color: '#616161',
		fontFamily: FontFamily.regular,
	},
});

export default DetailBarChart;
